import React from "react"
import { Form, Input, Select } from "antd"

import { useProjectStore } from "stores/projectStore"

import DataQButton from "shared/ui/Buttons/DataQButton.component"

import { useUpdateProjectMutation } from "features/Projects/api/useProjectsMutations"

import { UpdateProjectBody } from "types/project.types"

// Define the props interface
interface GeneralInformationTabProps {
  refreshProject: () => void
}

// Define the form values interface
interface FormValues {
  Name: string
  Goal: string
  Status: string
  Type: string
  Designlanguage: string
  languages: string[]
}

/**
 * GeneralInformationTab component - Renders a form for editing general information of a project.
 *
 * This component displays a form populated with the current project's information, allowing the user
 * to edit and save changes. It utilizes Ant Design's Form component for UI consistency and validation.
 * The form includes fields for project name, goal, status, type, design language, and programming languages.
 * Upon submission, it calls the `updateUserProject` function with the updated project details.
 *
 * @component
 * @param props - The component props.
 * @returns The GeneralInformationTab component, rendering the editable form.
 */
const GeneralInformationTab: React.FC<GeneralInformationTabProps> = (props) => {
  const { refreshProject } = props
  const [form] = Form.useForm<FormValues>()
  const updateProjectMutation = useUpdateProjectMutation()
  const project = useProjectStore((state) => state.project)

  /**
   * Handles form submission to save changes.
   *
   * This function is called when the form is submitted. It constructs a request body from the form values
   * and calls the `updateUserProject` API function. If the update is successful, it shows a success notification
   * and refreshes the project details. If an error occurs, it shows an error notification.
   *
   * @async
   * @function
   * @param formValues - The values of the form fields.
   */
  const saveChanges = async (formValues: FormValues): Promise<void> => {
    const body: UpdateProjectBody = {
      id: project.id,
      name: formValues.Name,
      goal: formValues.Goal,
      status: formValues.Status,
      type: formValues.Type,
      designLanguage: formValues.Designlanguage,
      languages: formValues.languages,
    }

    try {
      await updateProjectMutation.mutateAsync(body)
      refreshProject()
    } catch (error) {
      // Notifications are handled by the mutation; just log.
      console.error("Error updating project:", error)
    }
  }

  return (
    <div className="update-project-form bg-gradient-to-br from-gray-50 to-white rounded-b-2xl h-full border-t border-gray-100">
      <div className="bg-white h-full shadow-sm border-gray-200 p-6 flex flex-col rounded-b-2xl overflow-auto">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 m-0 mb-1">
            General Information
          </h2>
          <p className="text-sm text-gray-600">
            Manage your project's basic settings and configuration
          </p>
        </div>

        <Form
          form={form}
          name="update-project-form"
          onFinish={saveChanges}
          layout="vertical"
          className="space-y-0 flex-1 flex flex-col"
          requiredMark={false}
          initialValues={{
            languages: project.Languages,
            Name: project.Name,
            Designlanguage: project.Designlanguage,
            Goal: project.Goal,
            Status: project.Status,
            Type: project.Type.toUpperCase(),
          }}
        >
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Project Name
                    </span>
                  }
                  validateTrigger="onSubmit"
                  name="Name"
                  rules={[
                    {
                      required: true,
                      message: "Please Enter a Name",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter project name"
                    className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </Form.Item>

                <Form.Item
                  name="Status"
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Status
                    </span>
                  }
                >
                  <Input
                    size="large"
                    placeholder="e.g., In Progress, Completed"
                    className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Type
                    </span>
                  }
                  name="Type"
                >
                  <Input
                    size="large"
                    placeholder="Project type"
                    disabled
                    className="rounded-lg bg-gray-50 border-gray-200"
                  />
                </Form.Item>
              </div>

              <div className="space-y-4">
                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Design Language
                    </span>
                  }
                  validateTrigger="onSubmit"
                  name="Designlanguage"
                  rules={[
                    {
                      required: true,
                      message: "Please Enter a Design Language",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="e.g., English, Spanish"
                    className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </Form.Item>

                <Form.Item
                  name="languages"
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Languages
                    </span>
                  }
                >
                  <Select
                    size="large"
                    mode="tags"
                    placeholder="Select or add languages"
                    className="rounded-lg"
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="mt-6 col-span-full">
              <Form.Item
                label={
                  <span className="text-sm font-medium text-gray-700">
                    Project Goal
                  </span>
                }
                validateTrigger="onSubmit"
                name="Goal"
                rules={[
                  {
                    required: false,
                    message: "Please enter a project goal",
                  },
                ]}
              >
                <Input.TextArea
                  size="large"
                  placeholder="Describe the main objectives and goals of this project..."
                  rows={3}
                  className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  style={{
                    resize: "none",
                    fontSize: "14px",
                  }}
                />
              </Form.Item>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200 mt-auto">
            <DataQButton
              type="primary"
              htmlType="submit"
              isLoading={updateProjectMutation.isPending}
              className="px-8 py-2 font-medium"
            >
              Save Changes
            </DataQButton>
          </div>
        </Form>
      </div>
    </div>
  )
}

export default GeneralInformationTab
